import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useDesigners, useRenameProject, useUpdateConnection } from "../hooks";
import type { PowerProject } from "../types";
import { apiErr } from "../utils";

// PUWG 2000 zones used across the ZE portfolio (mirrors backend ZoneSrid choices).
const SRID_OPTIONS = [2176, 2177, 2178, 2179];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="section-label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// Edit the core project fields from the workspace header (CEO / dyrektor techniczny). The name lives
// on the hub przedsięwzięcie (separate, validated endpoint); EPSG / umowa / projektant live on the ZE
// profile, so the two are saved through their own mutations.
export function EditProjectDialog({
  connection,
  onClose,
}: {
  connection: PowerProject;
  onClose: () => void;
}) {
  const rename = useRenameProject(connection.id);
  const update = useUpdateConnection(connection.id);
  const { data: users } = useInternalUsers();
  const { data: designers } = useDesigners();

  const [name, setName] = useState(connection.project_name);
  const [srid, setSrid] = useState(connection.srid);
  const [contractNumber, setContractNumber] = useState(connection.contract_number ?? "");
  const [designer, setDesigner] = useState(connection.designer ?? "");
  const [leadDesigner, setLeadDesigner] = useState(connection.lead_designer ?? "");
  const [coordinator, setCoordinator] = useState(connection.coordinator ?? "");
  // Ścieżka listowna WPW: próg dni tego projektu ("" = globalny). Zmiana wymaga potwierdzenia —
  // przesuwa termin automatycznej wysyłki listów już zakolejkowanych.
  const [mailDays, setMailDays] = useState(
    connection.wpw_mail_auto_days === null || connection.wpw_mail_auto_days === undefined ? "" : String(connection.wpw_mail_auto_days),
  );
  const [confirmMailDays, setConfirmMailDays] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const designerOptions = [
    { value: "", label: "— brak —" },
    ...(users ?? []).map((u) => ({ value: u.id, label: u.full_name || u.username })),
  ];

  // Projektant uprawniony główny — ze słownika, kategoria „projektant uprawniony" (tylko
  // aktywni; obecnie przypisany zostaje na liście, nawet jeśli został zdezaktywowany).
  const leadDesignerOptions = [
    { value: "", label: "— brak —" },
    ...(designers ?? [])
      .filter((d) => d.role === "licensed_designer" && (d.is_active || d.id === connection.lead_designer))
      .map((d) => ({ value: d.id, label: d.display_name })),
  ];

  // Opracowujący — ze słownika, kategoria „opracowujący" (koordynatorzy ELEKTIM).
  const coordinatorOptions = [
    { value: "", label: "— brak —" },
    ...(designers ?? [])
      .filter((d) => d.role === "coordinator" && (d.is_active || d.id === connection.coordinator))
      .map((d) => ({ value: d.id, label: d.display_name })),
  ];

  const saving = rename.isPending || update.isPending;

  const mailDaysValue = mailDays.trim() === "" ? null : Number(mailDays);
  const mailDaysChanged = mailDaysValue !== (connection.wpw_mail_auto_days ?? null);
  const mailDaysValid =
    mailDaysValue === null || (Number.isInteger(mailDaysValue) && mailDaysValue >= 1 && mailDaysValue <= 365);

  const submit = async () => {
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Nazwa przedsięwzięcia nie może być pusta.");
      return;
    }
    if (!mailDaysValid) {
      setError("Próg wysyłki listownej: liczba dni od 1 do 365 albo puste pole (wartość globalna).");
      return;
    }
    // Zmiana progu listów WPW idzie przez modal z potwierdzeniem (wpływa na terminy w kolejce).
    if (mailDaysChanged && !confirmMailDays) {
      setConfirmMailDays(true);
      return;
    }
    try {
      // Name lives on the hub Project — its own endpoint enforces uniqueness.
      if (trimmedName !== connection.project_name) {
        await rename.mutateAsync(trimmedName);
      }
      // EPSG / umowa / projektant live on the ZE profile.
      const patch: Record<string, unknown> = {};
      if (srid !== connection.srid) patch.srid = srid;
      if (contractNumber !== (connection.contract_number ?? "")) patch.contract_number = contractNumber;
      if ((designer || null) !== (connection.designer ?? null)) patch.designer = designer || null;
      if ((leadDesigner || null) !== (connection.lead_designer ?? null)) patch.lead_designer = leadDesigner || null;
      if ((coordinator || null) !== (connection.coordinator ?? null)) patch.coordinator = coordinator || null;
      if (mailDaysChanged) patch.wpw_mail_auto_days = mailDaysValue;
      if (Object.keys(patch).length) {
        await update.mutateAsync(patch as never);
      }
      onClose();
    } catch (e) {
      setError(apiErr(e));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-md shadow-hard-red"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-2.5 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          <h3 className="font-mono text-xs font-bold uppercase tracking-widest text-[var(--color-text-primary)]">
            Edytuj projekt
          </h3>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Nazwa przedsięwzięcia">
            <input
              autoFocus
              className="input-base"
              value={name}
              maxLength={255}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label="Strefa EPSG">
            <select
              className="input-base"
              value={srid}
              onChange={(e) => setSrid(Number(e.target.value))}
            >
              {SRID_OPTIONS.map((s) => (
                <option key={s} value={s}>EPSG:{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Nr umowy">
            <input
              className="input-base"
              value={contractNumber}
              onChange={(e) => setContractNumber(e.target.value)}
            />
          </Field>
          <Field label="Projektant">
            <SearchableSelect
              placeholder="— brak —"
              value={designer}
              options={designerOptions}
              onChange={(e) => setDesigner(e.target.value)}
            />
          </Field>
          <Field label="Projektant uprawniony główny">
            <SearchableSelect
              placeholder="— brak —"
              value={leadDesigner}
              options={leadDesignerOptions}
              onChange={(e) => setLeadDesigner(e.target.value)}
            />
          </Field>
          <Field label="Opracowujący (ELEKTIM)">
            <SearchableSelect
              placeholder="— brak —"
              value={coordinator}
              options={coordinatorOptions}
              onChange={(e) => setCoordinator(e.target.value)}
            />
          </Field>
          <Field label="Automatyczna wysyłka listu WPW po [dni]">
            <input
              type="number"
              min={1}
              max={365}
              className="input-base"
              value={mailDays}
              placeholder={`globalnie: ${connection.wpw_mail_auto_days_effective ?? 14}`}
              onChange={(e) => { setMailDays(e.target.value); setConfirmMailDays(false); }}
              aria-label="Automatyczna wysyłka listu WPW po dniach"
            />
            <span className="text-[10px] font-mono text-[var(--color-text-muted)]">
              Puste = wartość globalna (ustawia CEO). Obowiązuje: {connection.wpw_mail_auto_days_effective ?? 14} dni.
            </span>
          </Field>
          {confirmMailDays && mailDaysChanged && (
            <div className="border border-amber-400/60 bg-amber-50 dark:bg-amber-950/30 p-2 text-[11px] font-mono space-y-1"
                 data-testid="wpw-mail-days-confirm">
              <p className="text-[var(--color-text-primary)]">
                Zmieniasz próg automatycznej wysyłki listów WPW w tym projekcie na{" "}
                <b>{mailDaysValue === null ? `wartość globalną` : `${mailDaysValue} dni`}</b>. Terminy listów już
                zakolejkowanych przeliczą się przy najbliższym przebiegu. Potwierdzić?
              </p>
              <p className="text-[var(--color-text-muted)]">Kliknij „Zapisz” ponownie, aby potwierdzić.</p>
            </div>
          )}
          {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onClose}>Anuluj</Button>
            <Button size="sm" loading={saving} onClick={submit}>Zapisz</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
